import type { ComponentType } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Stack } from "@similarweb/ui-components/foundations";

import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalFilters, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import {
    requestAppsSignalsAccess,
    requestIntentSignalsAccess,
} from "app/pages/sales-intelligence/modules/signals/services/requestSignalTypeAccess";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import trackingService from "app/pages/sales-intelligence/sub-modules/signals/services/tracking/trackingService";
import {
    setManagementDeleteSignalId,
    setManagementEditSignalId,
    setPreselectedSignalType,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { selectAllSignalsIdsFromSource } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

import { useAccountSignalsPageTypes } from "../../hooks/useAccountSignalsPageTypes";
import { useAreSignalsCountsLoading } from "../../hooks/useAreSignalsCountsLoading";
import { useHighlightCreatedSignal } from "../../hooks/useHighlightCreatedSignal";
import { AccountSignalTypeSection } from "../AccountSignalTypeSection/AccountSignalTypeSection";
import { IntentSignalTypeSectionAction } from "../IntentSignalTypeSectionAction/IntentSignalTypeSectionAction";
import { IntentSignalTypeSectionMeta } from "../IntentSignalTypeSectionMeta/IntentSignalTypeSectionMeta";

type SectionExtras = {
    headerAction?: ComponentType;
    meta?: ComponentType;
    /** Per surface: intent opens the topic hook here, where the management modal submits a sales request. */
    onRequestAccess?: VoidFunction;
    /** Set when `onRequestAccess` is a silent sales request, which the section must acknowledge itself. */
    showsRequestSentState?: boolean;
};

const accountSignalTypeSectionExtras: Partial<Record<SignalType, SectionExtras>> = {
    [signalTypeMap.intent]: {
        headerAction: IntentSignalTypeSectionAction,
        meta: IntentSignalTypeSectionMeta,
        onRequestAccess: requestIntentSignalsAccess,
    },
    [signalTypeMap.apps]: {
        onRequestAccess: requestAppsSignalsAccess,
        showsRequestSentState: true,
    },
};

type AccountSignalsTypeSectionsProps = {
    registerSectionRef(signalType: SignalType): (element: HTMLElement | null) => void;
};

export const AccountSignalsTypeSections = ({ registerSectionRef }: AccountSignalsTypeSectionsProps) => {
    const dispatch = useDispatch();
    const { scrollableTypes, viewModel, isHighLevelAccount } = useAccountSignalsPageTypes();
    const isSignalsLoading = useAreSignalsCountsLoading();
    const signalIds = useSelector(selectAllSignalsIdsFromSource);
    const highlightedSignalId = useHighlightCreatedSignal(isSignalsLoading, signalIds);

    const handleSignalCreate = (signalType: SignalType): void => {
        trackingService.management.trackCreateSignalClick("signals management page", signalType);
        dispatch(setPreselectedSignalType(signalType));
    };

    const handleSignalEdit = <F extends SignalFilters>(signal: Readonly<UISignal<F>>): void => {
        trackingService.management.trackEditSignalClick(signal);
        dispatch(setManagementEditSignalId(signal.id));
    };

    const handleSignalDelete = <F extends SignalFilters>(signal: Readonly<UISignal<F>>): void => {
        trackingService.management.trackDeleteSignalClick(signal);
        dispatch(setManagementDeleteSignalId(signal.id));
    };

    return (
        <Stack
            gap="32px"
            width="100%"
            data-automation="account-signals-type-sections"
        >
            {scrollableTypes.map(({ type, icon }) => {
                const extras = accountSignalTypeSectionExtras[type];
                const isLocked = signalsService.isSignalTypeLocked(type);
                const HeaderAction = extras?.headerAction;
                const Meta = extras?.meta;
                const areTypeActionsEnabled = isHighLevelAccount && !isLocked;

                return (
                    <AccountSignalTypeSection
                        key={type}
                        signalType={type}
                        icon={icon}
                        signals={viewModel.getUISignalsOfType(type)}
                        highlightedSignalId={highlightedSignalId}
                        isLoading={isSignalsLoading}
                        sectionRef={registerSectionRef(type)}
                        areActionsEnabled={areTypeActionsEnabled}
                        isLocked={isLocked}
                        onRequestAccess={extras?.onRequestAccess}
                        showsRequestSentState={extras?.showsRequestSentState}
                        headerAction={HeaderAction && !isLocked ? <HeaderAction /> : undefined}
                        meta={Meta ? <Meta /> : undefined}
                        onSignalCreate={handleSignalCreate}
                        onSignalEdit={handleSignalEdit}
                        onSignalDelete={handleSignalDelete}
                    />
                );
            })}
        </Stack>
    );
};
